/**
 * Purpose: Displays a filename on one line, truncating the middle so the file
 * extension always stays visible.
 *
 * How it works:
 * 1. Splits the filename into base name and extension
 * 2. Renders the base name in a `truncate` box (CSS ellipsis) and the extension
 *    in a `shrink-0` box next to it
 * 3. The browser decides where to cut, so the cut lands on the real pixel
 *    boundary of whatever space the parent gives us
 *
 * Why not truncate by character count: a fixed character budget doesn't know
 * how wide those characters render. A 30-char name is ~250px at 16px, which no
 * longer fits inside a message bubble on a ~390px-wide phone — and because a
 * `white-space: nowrap` string has a min-content width equal to its full width,
 * that string became a hard floor that pushed the whole card past the bubble
 * and got clipped mid-glyph (issue #1131).
 *
 * The parent must allow shrinking (`min-w-0` on the flex/grid chain) and must
 * not be shrink-to-fit (`w-fit`) — see MessageAttachment's file card.
 */

import React from "react";
import { cn } from "@/lib/utils";

interface TruncatedFilenameProps {
  /** The complete filename to display */
  filename: string;
  /** Optional CSS class name for styling */
  className?: string;
}

/**
 * Longest suffix still treated as an extension. Anything longer is kept in the
 * truncatable part — a "name.thisisnotanextension" must not reserve unbounded
 * un-shrinkable width.
 */
const MAX_EXTENSION_LENGTH = 10;

/**
 * Splits a filename into the part that may be truncated and the extension that
 * must stay visible.
 *
 * No extension is claimed when there is no dot, when the dot leads the name
 * (dotfiles like `.gitignore`), when the dot is trailing, or when the suffix is
 * too long to be a plausible extension.
 */
function splitFilename(filename: string): { base: string; extension: string } {
  const lastDotIndex = filename.lastIndexOf(".");

  if (lastDotIndex <= 0 || lastDotIndex === filename.length - 1) {
    return { base: filename, extension: "" };
  }

  const extension = filename.substring(lastDotIndex);

  if (extension.length - 1 > MAX_EXTENSION_LENGTH) {
    return { base: filename, extension: "" };
  }

  return { base: filename.substring(0, lastDotIndex), extension };
}

/**
 * Displays a filename on a single line, ellipsizing the base name while
 * preserving the extension.
 */
export function TruncatedFilename({
  filename,
  className = "",
}: TruncatedFilenameProps): React.ReactElement {
  const { base, extension } = splitFilename(filename);

  return (
    // min-w-0 so this can shrink inside a flex parent; text-left because an
    // outgoing bubble sets text-right on the whole bubble and a right-aligned
    // filename reads as broken next to the file icon.
    <span className={cn("flex min-w-0 text-left", className)} title={filename}>
      <span className="truncate">{base}</span>
      {extension && <span className="shrink-0">{extension}</span>}
    </span>
  );
}

export { splitFilename };
